import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { captureEvent } from "@/lib/analyticsQueue";
import { toast } from "@/lib/toast";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCredits } from "@/contexts/CreditsContext";
import { useAuth } from "@/contexts/AuthContext";
import { useCreditCosts } from "@/hooks/useCreditCosts";
import { avatarCreditsPerSec, TALKING_AVATAR_PRICING } from "@shared/pricing/modelPricing";
import { useGeneratedVideos } from "@/hooks/useGeneratedVideos";
import { useDbBackedPreviewPair } from "@/hooks/useDbBackedPreviewPair";
import { useLightboxNavigation } from "@/hooks/useLightboxNavigation";
import { usePersistentPendingBoost } from "@/hooks/usePersistentPendingBoost";
import { useAnonymousGate } from "@/hooks/useAnonymousGate";
import {
  clearFlowState,
  useFlowPersistence,
} from "@/hooks/useFlowPersistence";
import { FLOW_KEYS } from "@/config/flowKeys";
import { apiDelete, apiGet, apiPost } from "@/lib/api/apiClient";
import { uploadImagesToR2 } from "@/lib/api/uploadImage";
import { useImageUpload } from "@/hooks/useImageUpload";
import { downloadMedia, shareMedia } from "@/lib/mediaUtils";
import { ANALYTICS_EVENTS, FLOW_IDS } from "@shared/schemas/analytics";
import { captureGenerationError } from "@/lib/sentry";
import { videoToDisplayMedia } from "@/lib/types/media";
import type { DisplayMedia } from "@/lib/types/media";
import type { VoiceOption } from "@/components/flowly/TalkingAvatarVoicePicker/TalkingAvatarVoicePicker";
import {
  TALKING_AVATAR_BACKGROUND_ANCHOR_ID,
  TALKING_AVATAR_DIALOGUE_ANCHOR_ID,
  TALKING_AVATAR_IMAGE_MODE_ANCHOR_ID,
  TALKING_AVATAR_PREVIEW_ANCHOR_ID,
  TALKING_AVATAR_STYLE_ANCHOR_ID,
  TALKING_AVATAR_VOICE_ANCHOR_ID,
} from "./constants";
import type {
  TalkingAvatarImageMode,
  TalkingAvatarStyle,
  TalkingAvatarSubjectType,
  TalkingAvatarTier,
  UploadedSource,
} from "./types";
import type {
  CloneVoiceRequest,
  CloneVoiceResponse,
  PreviewTalkingAvatarRequest,
  PreviewTalkingAvatarResponse,
  RenderTalkingAvatarRequest,
  RenderTalkingAvatarResponse,
  TalkingAvatarVoicesResponse,
  UploadUrlResponse,
} from "@shared/schemas/api";
import {
  TALKING_AVATAR_DEFAULT_IMAGE_MODEL_ID,
  type TalkingAvatarImageModelId,
} from "@/config/talkingAvatar";

// Pricing is derived from the SINGLE SOURCE OF TRUTH
// (server/src/shared/pricing/modelPricing.ts) so the "~N credits" quote here
// exactly equals what the server charges. The preview pair is priced from the
// chosen image model's canonical 1k credit_costs row (× 2) via useCreditCosts.
const SECONDS_PER_WORD = 0.5; // 2 words = 1 second (pricing only, not actual)
const MIN_VIDEO_SEC = 2;
const PREVIEW_PAIR_COUNT = 2;

export function estimateRenderCredits(wordCount: number, tier: TalkingAvatarTier): number {
  const estimatedSec = Math.max(MIN_VIDEO_SEC, Math.ceil(wordCount * SECONDS_PER_WORD));
  return TALKING_AVATAR_PRICING.ttsCredits + avatarCreditsPerSec(tier) * estimatedSec;
}

/** BYO-audio variant of estimateRenderCredits — no TTS cost (we skip
 *  ElevenLabs entirely), and the duration comes straight from the file
 *  the user uploaded instead of being inferred from word count. Same
 *  per-second avatar rate as the TTS path so 2 sec ≈ 4 words pricing. */
export function estimateRenderCreditsFromAudio(
  durationSec: number,
  tier: TalkingAvatarTier,
): number {
  const safeSec = Math.max(MIN_VIDEO_SEC, Math.ceil(durationSec));
  return avatarCreditsPerSec(tier) * safeSec;
}

let uploadIdCounter = 0;
const nextUploadId = () => `ta-upload-${Date.now()}-${++uploadIdCounter}`;

function scrollToAnchor(id: string): void {
  requestAnimationFrame(() => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

export function useTalkingAvatar() {
  const { t } = useLanguage();
  const { normalizeOrWarn } = useImageUpload();
  const { refreshCredits, handleCreditError, openPricingModal, deductCredits } = useCredits();
  const { profile } = useAuth();

  // Step state
  const [upload, setUpload] = useState<UploadedSource | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  // Step 2 — render the uploaded photo as-is ("source") or design a new
  // image from it ("generate"). null until the user picks; "source" skips
  // the type/style/background/preview steps entirely.
  const [imageMode, setImageMode] = useState<TalkingAvatarImageMode | null>(null);
  // Upload-guide popup (same pattern as ProfilePhotoUploadGuide) — shown
  // once after the first upload; `guideSeen` is persisted so it doesn't
  // return after a refresh or the auth wall.
  const [showUploadGuide, setShowUploadGuide] = useState(false);
  const [guideSeen, setGuideSeen] = useState(false);
  const guideSeenRef = useRef(guideSeen);
  guideSeenRef.current = guideSeen;
  const [subjectType, setSubjectType] = useState<TalkingAvatarSubjectType | null>("person");
  const [style, setStyle] = useState<TalkingAvatarStyle | null>(null);
  const [background, setBackground] = useState("");
  const [composition, setComposition] = useState("");
  // Preview-pair state + DB-backed batch derivation live in the shared hook
  // (see PIPELINE / useDbBackedPreviewPair below).
  const [dialogue, setDialogue] = useState("");
  const [motion, setMotion] = useState("");
  const [voices, setVoices] = useState<VoiceOption[]>([]);
  const [voicesLoading, setVoicesLoading] = useState(true);
  const [selectedVoiceId, setSelectedVoiceId] = useState<string | null>(null);
  const [tier, setTier] = useState<TalkingAvatarTier>("standard");
  const [isRendering, setIsRendering] = useState(false);
  // BYO-audio mode — user's pre-recorded audio replaces TTS. When set, the
  // dialogue textbox + voice picker are hidden and the render call sends
  // {audioUrl,audioDurationSec} instead of {dialogue,voiceId}.
  const [userAudio, setUserAudio] = useState<{
    url: string;
    durationSec: number;
  } | null>(null);
  const [isUserAudioModalOpen, setIsUserAudioModalOpen] = useState(false);
  const [isUploadingUserAudio, setIsUploadingUserAudio] = useState(false);
  // Step 6 has two mutually exclusive paths — typing a script (then pick a
  // voice in step 7) OR supplying their own audio (which skips step 7). The
  // "select" mode renders the two-card picker; the others render the active
  // input. Persisted so a refresh restores the user's pick.
  const [dialogueMode, setDialogueMode] = useState<"select" | "text" | "audio">(
    "select",
  );
  const [selectedMedia, setSelectedMedia] = useState<DisplayMedia | null>(null);
  // Bottom-bar picker — same 2-model lineup as Baseball Trend. Drives only
  // the Stage-1 preview pair; the Stage-2 Kling Avatar render is unaffected.
  const [imageModelId, setImageModelId] = useState<TalkingAvatarImageModelId>(
    TALKING_AVATAR_DEFAULT_IMAGE_MODEL_ID,
  );

  // Pipeline name MUST match what generateTalkingAvatarService.ts writes to
  // generated_images / generated_videos.pipeline ("talking_avatar"). Used for
  // the preview-pair store, gallery filtering, and the pending-count boost.
  const PIPELINE = "talking_avatar";

  // Preview pair — DB-backed, realtime-synced, refresh/auth/top-up resilient.
  // Same shared mechanism Baseball Trend uses; destructured with the original
  // names so the rest of this hook + the page component are untouched.
  const {
    previewUrls,
    selectedPreviewIndex,
    selectedHistoricalUrl,
    isPreviewing,
    previewStartedAt,
    pastPreviewImages,
    effectivePreviewUrl,
    selectByIndex: selectPreviewByIndex,
    selectHistorical: selectHistoricalPreview,
    clearPreviews,
    beginPreview,
    endPreview,
    persisted: previewPersisted,
    restorePersisted: restorePreviewPersisted,
  } = useDbBackedPreviewPair({ pipeline: PIPELINE });

  // Persist config AND the uploaded photo across the auth/credit wall.
  // `upload` is an image key → its File bytes survive in IndexedDB (no longer
  // dependent on a post-generate R2 URL an anon user never has). The preview
  // pair itself is server state (rebuilt from the realtime store); we only
  // persist the user's PICK via `...previewPersisted`.
  useFlowPersistence({
    key: FLOW_KEYS.TALKING_AVATAR,
    state: {
      upload,
      imageMode,
      guideSeen,
      style,
      background,
      composition,
      dialogue,
      motion,
      selectedVoiceId,
      tier,
      imageModelId,
      dialogueMode,
      ...previewPersisted,
    },
    imageKeys: ["upload"],
    onRestore: (s) => {
      if (s.upload && typeof s.upload === "object") {
        setUpload(s.upload as UploadedSource);
      }
      if (s.imageMode === "source" || s.imageMode === "generate") {
        setImageMode(s.imageMode);
      } else if (s.subjectType || s.style || s.background) {
        // Draft saved before image-mode existed: it already carries
        // generate-path data, so adopt that path — otherwise its style /
        // background / preview steps would vanish behind the new step-2 gate.
        setImageMode("generate");
      }
      if (typeof s.guideSeen === "boolean") setGuideSeen(s.guideSeen);
      // subjectType is no longer user-selectable (fixed to "person"); not restored.
      if (s.style) setStyle(s.style as TalkingAvatarStyle);
      if (typeof s.background === "string") setBackground(s.background);
      if (typeof s.composition === "string") setComposition(s.composition);
      if (typeof s.dialogue === "string") setDialogue(s.dialogue);
      if (typeof s.motion === "string") setMotion(s.motion);
      if (typeof s.selectedVoiceId === "string") setSelectedVoiceId(s.selectedVoiceId);
      if (s.tier === "standard" || s.tier === "pro") setTier(s.tier);
      if (s.imageModelId === "nano-banana-2" || s.imageModelId === "gpt-image-2") {
        setImageModelId(s.imageModelId);
      }
      // userAudio itself isn't persisted (it's a transient R2 URL) — but if
      // mode was "audio", reset back to "select" since the audio is gone.
      if (s.dialogueMode === "text" || s.dialogueMode === "select") {
        setDialogueMode(s.dialogueMode);
      }
      restorePreviewPersisted(s);
    },
  });

  const {
    completedVideos,
    pendingVideos: dbPendingVideos,
    completingIds: videoCompletingIds,
    loading: videosLoading,
  } = useGeneratedVideos(PIPELINE);

  const { pendingCount, increment: incrementPending, decrement: decrementPending } =
    usePersistentPendingBoost({
      pipeline: PIPELINE,
      dbPendingCount: dbPendingVideos.length,
      isDbLoading: videosLoading,
    });

  const galleryVideos: DisplayMedia[] = useMemo(
    () => completedVideos.filter((v) => v.video_url).map(videoToDisplayMedia),
    [completedVideos],
  );

  const { currentMediaIndex, canNavigate, goToNextItem, goToPrevItem } =
    useLightboxNavigation(galleryVideos, selectedMedia, setSelectedMedia);

  /** Scroll target for the gallery section — focused after "Create video". */
  const galleryRef = useRef<HTMLDivElement>(null);

  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  /** Which CTA opened the modal — drives whether the file picker auto-opens
   *  on mount. "record" = default recording UI; "upload" = file picker
   *  fires immediately so the user skips the unnecessary intermediate step. */
  const [cloneModalAction, setCloneModalAction] = useState<"record" | "upload">("record");
  const [isCloning, setIsCloning] = useState(false);
  /** Word cap returned by the server based on the user's plan.
   *  Free/Starter → 60, Pro/Creator → 90. Defaults to 60 until /voices
   *  resolves so we never accidentally let a Free user past their cap. */
  const [maxWords, setMaxWords] = useState(60);
  /** Server-driven minimum word count. Kept here so we never duplicate the
   *  4-word floor in frontend code — single source of truth lives in the
   *  server's TALKING_AVATAR_MIN_WORDS constant. Defaults to 4 to match
   *  the server-side constant before /voices resolves. */
  const [minWords, setMinWords] = useState(4);
  /** Toggled when the user tries to type past maxWords — drives the upgrade
   *  modal that explains the cap and links to /pricing. */
  const [wordLimitModalOpen, setWordLimitModalOpen] = useState(false);

  // Fetch voice presets + clone status + word cap once on mount.
  // Re-fetched after every successful clone/delete so the picker updates
  // without a page reload.
  const reloadVoices = useCallback(async () => {
    // Optional auth: anonymous visitors get the preset voices (so the picker
    // is populated while browsing the flow); signed-in users additionally get
    // their cloned voice + plan word-cap. Never throws "Not authenticated".
    const res = await apiGet<TalkingAvatarVoicesResponse>(
      "/api/talking-avatar/voices",
      { auth: "optional" },
    );
    setVoices(res.voices as VoiceOption[]);
    setMaxWords(res.maxWords);
    setMinWords(res.minWords);
    // Default-select the first voice if none picked yet.
    setSelectedVoiceId((prev) => prev ?? res.voices[0]?.id ?? null);
  }, []);

  useEffect(() => {
    let canceled = false;
    (async () => {
      try {
        if (!canceled) await reloadVoices();
      } catch (err) {
        if (!canceled) {
          console.error("Error loading voices:", err);
          captureGenerationError(err, {
            flow: FLOW_IDS.TALKING_AVATAR,
            action: "voices_load",
            endpoint: "/api/talking-avatar/voices",
          });
          toast.error(err instanceof Error ? err.message : "Failed to load voices");
        }
      } finally {
        if (!canceled) setVoicesLoading(false);
      }
    })();
    return () => {
      canceled = true;
    };
  }, [reloadVoices]);

  // --- Analytics: tool page view ---
  useEffect(() => {
    captureEvent(ANALYTICS_EVENTS.TOOL_PAGE_VIEW, { flow: FLOW_IDS.TALKING_AVATAR });
  }, []);

  // Step 1: Upload — stays local. The File is kept in memory with a blob
  // preview; R2 upload is deferred until the user clicks "Create" (i.e. past
  // the anonymous gate). Anonymous browsing can attach an image with zero
  // network cost; signed-in users pay the upload latency only once, folded
  // into the spinner they'd see anyway during preview generation.
  const handleAddFile = useCallback(
    async (rawFile: File) => {
      const file = await normalizeOrWarn(rawFile);
      if (!file) return;
      captureEvent(ANALYTICS_EVENTS.IMAGE_UPLOAD, {
        flow: FLOW_IDS.TALKING_AVATAR,
      });
      const previewUrl = URL.createObjectURL(file);
      const id = nextUploadId();
      setUpload({ id, publicUrl: "", previewUrl, file });
      // Reset downstream — new photo invalidates later steps.
      clearPreviews();
      scrollToAnchor(TALKING_AVATAR_IMAGE_MODE_ANCHOR_ID);
      // First upload ever → show the "how to shoot it" guide popup (same
      // pattern as ProfilePhotoUploadGuide). Persisted so it shows once.
      if (!guideSeenRef.current) {
        setShowUploadGuide(true);
        setGuideSeen(true);
      }
    },
    [clearPreviews, normalizeOrWarn],
  );
  const closeUploadGuide = useCallback(() => setShowUploadGuide(false), []);

  /** Lazy-upload the local File to R2 the first time we need a CDN URL.
   *  Returns the resolved publicUrl and persists it on `upload` so a follow-up
   *  generate doesn't re-upload the same file. Throws on failure so callers
   *  can surface the error to the user. */
  const ensureUploadedToR2 = useCallback(async (): Promise<string> => {
    if (!upload) throw new Error("No image to upload");
    if (upload.publicUrl) return upload.publicUrl;
    if (!upload.file) throw new Error("Missing local file for upload");
    setIsUploading(true);
    try {
      const [uploaded] = await uploadImagesToR2([upload.file]);
      setUpload((prev) =>
        prev && prev.id === upload.id
          ? { ...prev, publicUrl: uploaded.publicUrl }
          : prev,
      );
      return uploaded.publicUrl;
    } finally {
      setIsUploading(false);
    }
  }, [upload]);

  // Step 2: Image mode — "source" jumps straight to the dialogue step (the
  // uploaded photo itself gets lip-synced); "generate" continues into the
  // type/style/background/preview steps. Steps are gated by this value in
  // the page component, so no downstream reset is needed on switch.
  const handleImageModeChange = useCallback(
    (next: TalkingAvatarImageMode) => {
      setImageMode(next);
      captureEvent(ANALYTICS_EVENTS.FLOW_MODE_SELECTED, {
        flow: FLOW_IDS.TALKING_AVATAR,
        mode: next,
      });
      scrollToAnchor(
        next === "source"
          ? TALKING_AVATAR_DIALOGUE_ANCHOR_ID
          : TALKING_AVATAR_STYLE_ANCHOR_ID,
      );
    },
    [],
  );

  // Step 3 (generate path): Style — NO auto-preview. User clicks "Create characters".
  // subjectType is fixed to "person" (the flow only targets characters), so
  // there's no person/object step — the prompt always uses person framing.
  const handleStyleChange = useCallback(
    (next: TalkingAvatarStyle) => {
      setStyle(next);
      clearPreviews();
      scrollToAnchor(TALKING_AVATAR_BACKGROUND_ANCHOR_ID);
    },
    [clearPreviews],
  );

  // Step 4: Background — text input. Then manual button triggers preview gen.
  const handleBackgroundChange = useCallback((value: string) => {
    setBackground(value);
  }, []);
  // Optional composition details (pose / props / framing). Folded behind an
  // "Advanced instructions" toggle so it doesn't intimidate casual users.
  const handleCompositionChange = useCallback((value: string) => {
    setComposition(value);
  }, []);

  const generatePreview = useCallback(async () => {
    if (!upload || !subjectType || !style) return;
    beginPreview();
    try {
      // Lazy R2 upload: anonymous browsing kept the File local, so the very
      // first preview after signup is the moment we actually push bytes.
      const sourceImageUrl = await ensureUploadedToR2();
      const body: PreviewTalkingAvatarRequest = {
        sourceImageUrl,
        subjectType,
        style,
        backgroundDescription: background,
        composition: composition.trim() || undefined,
        imageModelId,
      };
      // Blocking endpoint: by the time it resolves the server has written
      // the completed generated_images rows; the realtime store surfaces
      // them as the current batch. Response body intentionally ignored.
      await apiPost<PreviewTalkingAvatarResponse>(
        "/api/talking-avatar/preview",
        body,
      );
      scrollToAnchor(TALKING_AVATAR_PREVIEW_ANCHOR_ID);
    } catch (err) {
      console.error("Error generating talking-avatar preview:", err);
      captureGenerationError(err, {
        flow: FLOW_IDS.TALKING_AVATAR,
        action: "preview",
        endpoint: "/api/talking-avatar/preview",
      });
      // Parity with old flows: insufficient credits → pricing modal;
      // refund-on-failure reflected via refreshCredits() in `finally`.
      if (!handleCreditError(err)) {
        toast.error(t.talkingAvatar.generationFailed, {
          description:
            err instanceof Error ? err.message : t.talkingAvatar.previewFailedDesc,
        });
      }
    } finally {
      endPreview();
      await refreshCredits();
    }
  }, [upload, subjectType, style, background, composition, imageModelId, ensureUploadedToR2, refreshCredits, handleCreditError, beginPreview, endPreview, t]);

  /** Switching image model invalidates an already-generated preview pair
   *  (different model = different look), so we clear them. Mirrors Baseball
   *  Trend's handleImageModelChange. */
  const handleImageModelChange = useCallback(
    (next: TalkingAvatarImageModelId) => {
      setImageModelId(next);
      clearPreviews();
    },
    [clearPreviews],
  );
  const handleGeneratePreview = useAnonymousGate(generatePreview);

  // Step 5: Preview
  const handlePreviewSelect = useCallback(
    (index: number) => {
      selectPreviewByIndex(index);
      scrollToAnchor(TALKING_AVATAR_DIALOGUE_ANCHOR_ID);
    },
    [selectPreviewByIndex],
  );

  /** User picked a preview from their history (NOT the current pair). */
  const handleHistoricalPreviewSelect = useCallback(
    (url: string) => {
      selectHistoricalPreview(url);
      scrollToAnchor(TALKING_AVATAR_DIALOGUE_ANCHOR_ID);
    },
    [selectHistoricalPreview],
  );

  // Step 6: Dialogue + optional motion/action prompt
  // Word cap is enforced HERE so the textarea can't even hold a value with
  // more words than the user's plan allows. If the new value would exceed
  // the cap, we (a) clip the input back to the previous in-state dialogue
  // (b) open the upgrade modal so the user understands why typing stopped.
  const handleDialogueChange = useCallback(
    (value: string) => {
      const words = value.trim().split(/\s+/).filter(Boolean).length;
      if (words > maxWords) {
        setWordLimitModalOpen(true);
        return; // reject the typed character(s)
      }
      setDialogue(value);
    },
    [maxWords],
  );
  const closeWordLimitModal = useCallback(() => setWordLimitModalOpen(false), []);
  const handleMotionChange = useCallback((value: string) => {
    setMotion(value);
  }, []);

  // Step 7: Voice + tier
  const handleVoiceSelect = useCallback((id: string) => {
    setSelectedVoiceId(id);
    scrollToAnchor(TALKING_AVATAR_VOICE_ANCHOR_ID);
  }, []);
  const handleTierChange = useCallback((next: TalkingAvatarTier) => {
    setTier(next);
  }, []);

  // Word count + estimated credits (recomputed live as the user types)
  const wordCount = useMemo(
    () => dialogue.trim().split(/\s+/).filter(Boolean).length,
    [dialogue],
  );
  const estimatedCredits = useMemo(() => {
    // BYO-audio mode: charge per second of the uploaded audio (no TTS cost).
    if (userAudio) return estimateRenderCreditsFromAudio(userAudio.durationSec, tier);
    return wordCount > 0 ? estimateRenderCredits(wordCount, tier) : 0;
  }, [wordCount, tier, userAudio]);

  // Preview pair cost = chosen model's canonical 1k credit_costs row × 2 —
  // the exact source the server charges from (no bespoke variant).
  const { getImageGenerateCost } = useCreditCosts();
  const previewCost = useMemo(
    () => getImageGenerateCost("1k", imageModelId) * PREVIEW_PAIR_COUNT,
    [getImageGenerateCost, imageModelId],
  );

  // `effectivePreviewUrl` (history pick ?? picked current-batch slot) is
  // provided by useDbBackedPreviewPair above.

  const canGenerate = useMemo(() => {
    if (!upload) return false;
    // Source mode lip-syncs the uploaded photo itself; every other mode
    // (including legacy persisted drafts with no mode) needs a picked preview.
    if (imageMode !== "source" && !effectivePreviewUrl) return false;
    if (isRendering || isPreviewing) return false;
    // BYO-audio mode bypasses dialogue/voice — the audio file IS the script.
    if (userAudio) return true;
    if (wordCount === 0) return false;
    // Min words = server's TALKING_AVATAR_MIN_WORDS (4 by default). Below this
    // the generated audio runs under 2 s and Kling Avatar rejects it with
    // `invalidDuration`. Block client-side so we never burn a TTS call.
    if (wordCount < minWords) return false;
    // Plan-specific cap from the server (60 Free, 90 Pro). The textarea
    // already prevents typing past it, but defense-in-depth in case state
    // gets out of sync.
    if (wordCount > maxWords) return false;
    if (!selectedVoiceId) return false;
    return true;
  }, [upload, imageMode, effectivePreviewUrl, wordCount, minWords, maxWords, selectedVoiceId, isRendering, isPreviewing, userAudio]);

  const generateVideo = useCallback(async () => {
    if (!canGenerate) return;
    if (imageMode !== "source" && !effectivePreviewUrl) return;
    // TTS mode requires a selected voice; BYO-audio mode requires the audio.
    if (!userAudio && !selectedVoiceId) return;

    // Local credit pre-check: open the upgrade modal instantly on click instead
    // of waiting for the server 402 (mirrors useImageGenerator). Server remains
    // the authoritative deduction; handleCreditError in catch is the backstop.
    if (!deductCredits(estimatedCredits)) return;

    setIsRendering(true);
    // Boost the pending count so the gallery shows a placeholder *now* — the
    // realtime subscription will pick up the DB row a beat later. The boost
    // survives page refresh via sessionStorage (usePersistentPendingBoost).
    incrementPending(1);
    setTimeout(() => {
      galleryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);

    let succeeded = false;
    try {
      // Source mode: the uploaded photo itself is the render input. It may
      // still be local-only (R2 upload is deferred past the anonymous gate),
      // so push it now — same lazy-upload moment the preview path uses.
      const imageUrl =
        imageMode === "source" ? await ensureUploadedToR2() : effectivePreviewUrl!;
      const body: RenderTalkingAvatarRequest = userAudio
        ? {
            imageUrl,
            audioUrl: userAudio.url,
            audioDurationSec: userAudio.durationSec,
            tier,
            motion: motion.trim() || undefined,
          }
        : {
            imageUrl,
            dialogue: dialogue.trim(),
            voiceId: selectedVoiceId!,
            tier,
            motion: motion.trim() || undefined,
          };
      await apiPost<RenderTalkingAvatarResponse>("/api/talking-avatar/render", body);
      succeeded = true;
      // The user got what they wanted — drop the persisted draft AND reset
      // inputs to defaults so the next visit (and the on-screen form) starts
      // fresh instead of replaying the exact same dialogue/voice/photo. The
      // rendered video shows in the DB-backed gallery, untouched here.
      clearFlowState(FLOW_KEYS.TALKING_AVATAR);
      if (upload) URL.revokeObjectURL(upload.previewUrl);
      setUpload(null);
      setImageMode(null);
      // subjectType stays "person" — it's fixed (no person/object step) and
      // generatePreview guards on it being truthy.
      setSubjectType("person");
      setStyle(null);
      setBackground("");
      setComposition("");
      clearPreviews();
      setDialogue("");
      setMotion("");
      setSelectedVoiceId(null);
      setUserAudio(null);
      setDialogueMode("select");
      setTier("standard");
      setImageModelId(TALKING_AVATAR_DEFAULT_IMAGE_MODEL_ID);
    } catch (err) {
      console.error("Error rendering talking avatar:", err);
      captureGenerationError(err, {
        flow: FLOW_IDS.TALKING_AVATAR,
        action: "render",
        endpoint: "/api/talking-avatar/render",
      });
      if (!handleCreditError(err)) {
        toast.error(t.talkingAvatar.generationFailed, {
          description:
            err instanceof Error ? err.message : t.talkingAvatar.generationFailedDesc,
        });
      }
    } finally {
      setIsRendering(false);
      if (!succeeded) decrementPending(1);
      await refreshCredits();
    }
  }, [canGenerate, upload, imageMode, ensureUploadedToR2, effectivePreviewUrl, dialogue, motion, selectedVoiceId, userAudio, tier, refreshCredits, handleCreditError, deductCredits, estimatedCredits, incrementPending, decrementPending, clearPreviews, t]);
  // The main Create-Video button is rendered via GenerationBar, whose
  // GenerateButton already gates anon clicks. So this is just a rename.
  // (The preview button above is a separate UI element outside GenerationBar,
  // hence its explicit useAnonymousGate wrap.)
  const handleGenerate = generateVideo;

  // Lightbox handlers
  const handleDownload = useCallback(async () => {
    if (!selectedMedia) return;
    await downloadMedia(selectedMedia.url, "todoai-talking-avatar", "mp4");
  }, [selectedMedia]);
  const handleShare = useCallback(() => {
    if (!selectedMedia?.id) return;
    void shareMedia({ id: selectedMedia.id, type: selectedMedia.type });
  }, [selectedMedia]);
  const handleCloseLightbox = useCallback(() => setSelectedMedia(null), []);
  /** Restore the source image (and dialogue if present) from the selected
   *  gallery video back into the upload slot, clear previews, close the
   *  lightbox, and scroll to top so the user can tweak settings and re-render.
   *  Mirrors the regenerate pattern in TextToVideo / FramesToVideo. */
  const handleRegenerate = useCallback(() => {
    const sourceUrl = selectedMedia?.referenceImageUrls[0];
    if (sourceUrl) {
      setUpload({
        id: nextUploadId(),
        publicUrl: sourceUrl,
        previewUrl: sourceUrl,
      });
    }
    clearPreviews();
    if (selectedMedia?.prompt) setDialogue(selectedMedia.prompt);
    setSelectedMedia(null);
    setTimeout(() => window.scrollTo({ top: 0, behavior: "smooth" }), 0);
  }, [selectedMedia, clearPreviews]);

  // ── Voice cloning ─────────────────────────────────────
  /** Free users see the clone CTA the same as paid users, but clicking it
   *  routes them to the upgrade flow instead of opening the recorder. Starter
   *  and above open the recorder normally. The server still enforces the gate
   *  on the actual `/clone-voice` call as defense-in-depth. */
  const handleOpenCloneModal = useCallback(
    (action: "record" | "upload" = "record") => {
      const plan = profile?.subscription_name ?? "free";
      if (plan === "free") {
        // Surface the plan-gate copy ("Feature available on Starter+")
        // instead of the default credit-wall copy of PricingModal.
        openPricingModal("starter_plan_required");
        return;
      }
      setCloneModalAction(action);
      setIsCloneModalOpen(true);
    },
    [profile?.subscription_name, openPricingModal],
  );
  const handleCloseCloneModal = useCallback(() => setIsCloneModalOpen(false), []);

  /** Same plan gate as the clone flow — Starter+ only. Free users get the
   *  pricing modal with the plan-gate copy. The server re-checks on render. */
  const handleOpenUserAudioModal = useCallback(() => {
    const plan = profile?.subscription_name ?? "free";
    if (plan === "free") {
      openPricingModal("starter_plan_required");
      return;
    }
    setIsUserAudioModalOpen(true);
  }, [profile?.subscription_name, openPricingModal]);
  const handleCloseUserAudioModal = useCallback(
    () => setIsUserAudioModalOpen(false),
    [],
  );

  /** Upload the user's audio to R2 (existing presigned-URL flow) and store
   *  the public URL + duration in state. The render call later sends those
   *  to the server, which skips TTS entirely. */
  /** User picked "text" path on the step-6 selector — wipe any audio they
   *  had so the two paths stay mutually exclusive. */
  const handleSelectTextMode = useCallback(() => {
    setUserAudio(null);
    setDialogueMode("text");
  }, []);

  /** "Switch path" button — reset both inputs and return to the picker. */
  const handleSwitchDialogueMode = useCallback(() => {
    setUserAudio(null);
    setDialogue("");
    setSelectedVoiceId(null);
    setDialogueMode("select");
  }, []);

  const handleUserAudioSubmit = useCallback(
    async (params: { audio: Blob; filename: string; durationSec: number }) => {
      setIsUploadingUserAudio(true);
      try {
        // Map to a MIME that Kling Avatar accepts (mp3/wav/m4a/aac/mpeg).
        // Specifically m4a → "audio/m4a" not "audio/mp4" — Kling rejects the
        // latter even though they're the same container.
        const ext = (params.filename.split(".").pop() ?? "mp3").toLowerCase();
        const contentType =
          ext === "wav"
            ? "audio/wav"
            : ext === "m4a" || ext === "mp4"
              ? "audio/m4a"
              : ext === "aac"
                ? "audio/aac"
                : "audio/mpeg";
        const { urls } = await apiPost<UploadUrlResponse>("/api/upload-url", {
          count: 1,
          contentType,
        });
        const { presignedUrl, publicUrl } = urls[0];
        const putRes = await fetch(presignedUrl, {
          method: "PUT",
          headers: { "Content-Type": contentType },
          body: params.audio,
        });
        if (!putRes.ok) throw new Error(`R2 upload failed: ${putRes.status}`);
        setUserAudio({ url: publicUrl, durationSec: params.durationSec });
        // Lock the step into BYO-audio mode — hides the script box and
        // skips step 7 (voice picker) entirely.
        setDialogueMode("audio");
        setDialogue("");
        setSelectedVoiceId(null);
      } catch (err) {
        console.error("Error uploading user audio:", err);
        captureGenerationError(err, {
          flow: FLOW_IDS.TALKING_AVATAR,
          action: "user_audio_upload",
          endpoint: "/api/upload-url",
        });
        toast.error(t.talkingAvatar.generationFailed, {
          description: err instanceof Error ? err.message : undefined,
        });
        throw err; // keep the modal open on failure
      } finally {
        setIsUploadingUserAudio(false);
      }
    },
    [t],
  );

  const handleClearUserAudio = useCallback(() => {
    setUserAudio(null);
    setDialogueMode("select");
  }, []);

  const handleCloneSubmit = useCallback(
    async (params: { audio: Blob; filename: string; name: string; gender: "male" | "female" }) => {
      setIsCloning(true);
      try {
        // Upload the sample to R2 via the existing presigned-URL endpoint.
        const ext = (params.filename.split(".").pop() ?? "mp3").toLowerCase();
        const contentType =
          ext === "wav"
            ? "audio/wav"
            : ext === "m4a"
              ? "audio/mp4"
              : ext === "webm"
                ? "audio/webm"
                : "audio/mpeg";
        const { urls } = await apiPost<UploadUrlResponse>("/api/upload-url", {
          count: 1,
          contentType,
        });
        const { presignedUrl, publicUrl } = urls[0];
        const putRes = await fetch(presignedUrl, {
          method: "PUT",
          headers: { "Content-Type": contentType },
          body: params.audio,
        });
        if (!putRes.ok) throw new Error(`R2 upload failed: ${putRes.status}`);

        // Call clone endpoint with the resulting public URL.
        const body: CloneVoiceRequest = {
          sampleUrl: publicUrl,
          name: params.name,
          gender: params.gender,
        };
        await apiPost<CloneVoiceResponse>("/api/talking-avatar/clone-voice", body);

        toast.success(t.talkingAvatar.cloneSuccessToast);
        await reloadVoices();
      } catch (err) {
        console.error("Error cloning voice:", err);
        captureGenerationError(err, {
          flow: FLOW_IDS.TALKING_AVATAR,
          action: "voice_clone",
          endpoint: "/api/talking-avatar/clone-voice",
        });
        if (!handleCreditError(err)) {
          toast.error(t.talkingAvatar.cloneErrorToast, {
            description: err instanceof Error ? err.message : undefined,
          });
        }
        throw err; // re-throw so the modal stays open on failure
      } finally {
        setIsCloning(false);
        await refreshCredits();
      }
    },
    [reloadVoices, refreshCredits, handleCreditError, t],
  );

  const handleDeleteClonedVoice = useCallback(async () => {
    if (!window.confirm(t.talkingAvatar.cloneDeleteConfirm)) return;
    try {
      await apiDelete("/api/talking-avatar/clone-voice");
      // Reset selection if the deleted voice was active.
      const cloned = voices.find((v) => v.isCloned);
      if (cloned && selectedVoiceId === cloned.id) {
        setSelectedVoiceId(voices.find((v) => !v.isCloned)?.id ?? null);
      }
      await reloadVoices();
    } catch (err) {
      console.error("Error deleting cloned voice:", err);
      captureGenerationError(err, {
        flow: FLOW_IDS.TALKING_AVATAR,
        action: "voice_delete",
        endpoint: "/api/talking-avatar/clone-voice",
      });
      toast.error(err instanceof Error ? err.message : "Delete failed");
    }
  }, [reloadVoices, voices, selectedVoiceId, t]);

  // Voice-clone and voice-delete are authed actions outside GenerationBar, so
  // they need explicit gates. We gate the **open-modal** handler (not the
  // submit) so anon users see the signup modal before they record audio —
  // and the clone modal's await-flow on submit is unaffected. Delete is a
  // fire-and-forget click, so we gate it directly.
  const gatedHandleOpenCloneModal = useAnonymousGate(handleOpenCloneModal);
  const gatedHandleDeleteClonedVoice = useAnonymousGate(handleDeleteClonedVoice);

  return {
    // State
    upload,
    isUploading,
    imageMode,
    /** Upload-guide popup — shown once after the first upload. */
    showUploadGuide,
    closeUploadGuide,
    style,
    background,
    composition,
    previewUrls,
    selectedPreviewIndex,
    selectedHistoricalUrl,
    pastPreviewImages,
    isPreviewing,
    previewStartedAt,
    dialogue,
    motion,
    voices,
    voicesLoading,
    selectedVoiceId,
    tier,
    isRendering,
    estimatedCredits,
    wordCount,
    /** Server-resolved word cap (60 Free, 90 Pro). */
    maxWords,
    /** Server-resolved word floor (default 4). Below this Kling rejects the
     *  audio for being under 2 s. */
    minWords,
    /** Modal that appears when the user tries to type past `maxWords`. */
    wordLimitModalOpen,
    closeWordLimitModal,
    previewCost,

    // Bottom-bar image-model picker (Stage-1 preview pair only)
    imageModelId,
    handleImageModelChange,

    // Voice cloning
    isCloneModalOpen,
    cloneModalAction,
    isCloning,

    // BYO-audio (Bring Your Own audio) — replaces dialogue+voice when set.
    userAudio,
    isUserAudioModalOpen,
    isUploadingUserAudio,
    /** "select" → render the two-path picker; "text" → script box + voice;
     *  "audio" → BYO audio card (step 7 hidden). */
    dialogueMode,

    // Gallery
    galleryRef,
    galleryVideos,
    pendingVideoCount: pendingCount,
    dbPendingVideos,
    videoCompletingIds,
    videosLoading,
    selectedMedia,
    setSelectedMedia,
    currentMediaIndex,
    canNavigate,
    goToNextItem,
    goToPrevItem,

    // Handlers
    handleAddFile,
    handleImageModeChange,
    handleStyleChange,
    handleBackgroundChange,
    handleCompositionChange,
    handleGeneratePreview,
    handlePreviewSelect,
    handleHistoricalPreviewSelect,
    handleDialogueChange,
    handleMotionChange,
    handleVoiceSelect,
    handleTierChange,
    handleGenerate,

    // Lightbox
    handleDownload,
    handleShare,
    handleCloseLightbox,
    handleRegenerate,

    // Voice clone handlers (open + delete gated at the button; submit stays
    // un-gated because once the modal is open the user is already signed in).
    handleOpenCloneModal: gatedHandleOpenCloneModal,
    handleCloseCloneModal,
    handleCloneSubmit,
    handleDeleteClonedVoice: gatedHandleDeleteClonedVoice,

    // BYO-audio handlers (gate happens inside the open handler — same as clone).
    handleOpenUserAudioModal,
    handleCloseUserAudioModal,
    handleUserAudioSubmit,
    handleClearUserAudio,
    handleSelectTextMode,
    handleSwitchDialogueMode,

    // Derived
    canGenerate,

    // i18n
    t,
  };
} 
